/**
 * `window.__hitopAuto` を mount/unmount するブリッジフック。
 *
 * App.tsx で 1 回だけ呼ぶ。Zustand store と usePipeline を結線して
 * 外部から自動操作可能にする。UI / UX は完全に不変。
 */

import { useEffect } from 'react';

import {
  HITOP_AUTO_API_VERSION,
  type HitopAutoApi,
  type HitopAutoResult,
  type HitopVideoResult,
} from '../lib/automation';
import { runPipeline } from '../lib/pipeline/run-all';
import { buildVideoFromZip } from '../lib/video/video-pipeline';
import { validateMetadata } from '../stores/metadata-validation';
import { type MetadataInput, toVehicleMetadata, useAppStore } from '../stores/useAppStore';

const POLL_INTERVAL_MS = 250;

export function useAutomationBridge(): void {
  useEffect(() => {
    const api: HitopAutoApi = {
      version: HITOP_AUTO_API_VERSION,

      getPhase: () => useAppStore.getState().phase.kind,

      canStart: () => {
        const s = useAppStore.getState();
        return s.phase.kind === 'idle' && s.files.length > 0 && s.files.length <= 80;
      },

      getResult: () => buildResult(),

      loadFilesFromUrls: async (urls) => {
        const files: File[] = [];
        for (const url of urls) {
          const r = await fetch(url);
          if (!r.ok) throw new Error(`fetch ${url} failed: ${r.status}`);
          const blob = await r.blob();
          const name = url.split('/').pop() || 'photo.jpg';
          const type = blob.type || guessTypeFromName(name);
          files.push(new File([blob], name, { type }));
        }
        useAppStore.getState().addFiles(files);
        return useAppStore.getState().files.length;
      },

      loadFilesFromBlobs: async (items) => {
        const files: File[] = items.map(
          ({ name, blob, type }) => new File([blob], name, { type: type || blob.type }),
        );
        useAppStore.getState().addFiles(files);
        return useAppStore.getState().files.length;
      },

      setMetadata: (patch: Partial<MetadataInput>) => {
        useAppStore.getState().setMetadata(patch);
      },

      setHeroIndex: (index: number) => {
        useAppStore.getState().setHeroIndex(index);
      },

      clearFiles: () => {
        useAppStore.getState().clearFiles();
      },

      start: async () => {
        // imperative ストア参照で常に最新状態を読む（クロージャキャプチャ回避）
        const s = useAppStore.getState();
        if (s.phase.kind !== 'idle' || s.files.length === 0) {
          throw new Error(`start: not ready (phase=${s.phase.kind}, files=${s.files.length})`);
        }
        s.setPhase({ kind: 'validating' });
        const validation = validateMetadata(s.metadata);
        if (!validation.ok) {
          const errs = Object.values(validation.errors).filter((v): v is string => Boolean(v));
          s.setErrors(errs);
          s.setPhase({ kind: 'idle' });
          throw new Error(`validation failed: ${errs.join('; ')}`);
        }
        s.setErrors([]);
        const metadata = toVehicleMetadata(s.metadata);
        try {
          const result = await runPipeline({
            files: s.files,
            heroIndex: s.heroIndex,
            metadata,
            onProgress: (p) =>
              s.setPhase({
                kind: 'processing',
                step: p.step,
                current: p.current,
                total: p.total,
              }),
          });
          s.setPhase({
            kind: 'done',
            zipBlob: result.zipBlob,
            zipName: result.zipName,
            summary: {
              totalPhotos: result.totalPhotos,
              heroFallbackUsed: result.heroFallbackUsed,
              cleanedCount: result.cleanedCount,
              elapsedMs: result.elapsedMs,
            },
          });
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          s.setPhase({ kind: 'error', message });
          throw err;
        }
      },

      reset: () => {
        useAppStore.getState().reset();
      },

      generateVideo: async (onProgress): Promise<HitopVideoResult> => {
        const phase = useAppStore.getState().phase;
        if (phase.kind !== 'done') {
          throw new Error(`generateVideo: phase must be 'done' (current: ${phase.kind})`);
        }
        const zipName = phase.zipName ?? 'output.zip';
        const mp4Name = zipName.replace(/\.zip$/i, '.mp4');
        const result = await buildVideoFromZip({
          zipBlob: phase.zipBlob,
          outputName: mp4Name,
          onProgress: (p) => onProgress?.(p.overallRatio, p.message ?? p.phase),
        });
        return {
          blob: result.blob,
          name: mp4Name,
          sizeBytes: result.fileSizeBytes,
          durationSec: result.durationSec,
        };
      },

      waitForDone: (timeoutMs = 600_000) =>
        new Promise<HitopAutoResult>((resolve, reject) => {
          const startedAt = Date.now();
          const tick = (): void => {
            const phase = useAppStore.getState().phase;
            if (phase.kind === 'done' || phase.kind === 'error') {
              const result = buildResult();
              if (result) resolve(result);
              else reject(new Error('result missing'));
              return;
            }
            if (Date.now() - startedAt > timeoutMs) {
              reject(new Error(`waitForDone timeout (${timeoutMs}ms)`));
              return;
            }
            setTimeout(tick, POLL_INTERVAL_MS);
          };
          tick();
        }),
    };

    window.__hitopAuto = api;

    return () => {
      if (window.__hitopAuto === api) {
        delete window.__hitopAuto;
      }
    };
  }, []);
}

function buildResult(): HitopAutoResult | null {
  const phase = useAppStore.getState().phase;
  if (phase.kind === 'done') {
    return {
      phase: 'done',
      zipBlob: phase.zipBlob,
      zipName: phase.zipName,
      summary: phase.summary,
    };
  }
  if (phase.kind === 'error') {
    return {
      phase: 'error',
      errorMessage: phase.message,
    };
  }
  return null;
}

function guessTypeFromName(name: string): string {
  const lower = name.toLowerCase();
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  return 'application/octet-stream';
}
